import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Logo } from "../../components/common/Logo";
import { authService } from "../../lib/api/auth";
import { useAuthStore } from "../../stores/authStore";
import { useTranslation } from "react-i18next";

type VerificationState = 'loading' | 'error' | 'no-token';

export const VerifyEmailPage = () => {
    const { t } = useTranslation("common");
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthStore();

    const [state, setState] = useState<VerificationState>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');


    // Ref to prevent multiple verification attempts
    const hasVerified = useRef(false);

    const token = searchParams.get('token');

    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    // Cooldown countdown
    useEffect(() => {
        if (cooldownSeconds <= 0) return;
        const timer = setInterval(() => {
            setCooldownSeconds(s => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldownSeconds]);

    // Check initial cooldown status for authenticated users
    useEffect(() => {
        if (isAuthenticated) {
            authService.getVerificationStatus().then(status => {
                if (status.data.canResendAfter) {
                    const canResendAt = new Date(status.data.canResendAfter).getTime();
                    const now = Date.now();
                    if (canResendAt > now) {
                        setCooldownSeconds(Math.ceil((canResendAt - now) / 1000));
                    }
                }
            }).catch(err => console.error('Failed to check status', err));
        }
    }, [isAuthenticated]);

    const handleResend = async () => {
        setIsResending(true);
        setResendSuccess(false);
        try {
            const response = await authService.resendVerification();
            setResendSuccess(true);
            setTimeout(() => setResendSuccess(false), 5000);

            if (response.data.canResendAfter) {
                const canResendAt = new Date(response.data.canResendAfter).getTime();
                setCooldownSeconds(Math.ceil((canResendAt - Date.now()) / 1000));
            } else {
                setCooldownSeconds(60); // Fallback
            }
        } catch (error: any) {
            const message = error.response?.data?.error || error.message || 'Failed to resend email';
            setErrorMessage(message);

            // Extract cooldown from error if rate limited
            const match = message.match(/wait (\d+) seconds/);
            if (match) {
                setCooldownSeconds(parseInt(match[1], 10));
            }
        } finally {
            setIsResending(false);
        }
    };

    useEffect(() => {
        const urlStatus = searchParams.get('status');
        const urlMessage = searchParams.get('message');

        if (urlStatus === 'no-token') {
            setState('no-token');
            return;
        }

        if (urlStatus === 'error') {
            setState('error');
            if (urlMessage) setErrorMessage(decodeURIComponent(urlMessage));
            else setErrorMessage(String(t("auth.verificationFailed", "Verification failed")));
            hasVerified.current = true;
            return;
        }

        if (!token) {
            setState('no-token');
            return;
        }

        // Prevent multiple verification attempts if we're already loading or have tried
        if (hasVerified.current) {
            return;
        }
        hasVerified.current = true;

        const verifyEmail = async () => {
            try {
                const response = await authService.verifyEmail(token);

                if (response.success) {


                    // Update user in store if logged in
                    const currentState = useAuthStore.getState();
                    if (currentState.isAuthenticated && currentState.user) {
                        currentState.setUser({
                            ...currentState.user,
                            emailVerified: true,
                            emailVerifiedAt: response.data.user.emailVerifiedAt || new Date().toISOString()
                        });
                    }

                    // Redirect directly to the appropriate page
                    if (currentState.isAuthenticated) {
                        navigate('/boards', { replace: true });
                    } else {
                        navigate('/?modal=login', { replace: true });
                    }
                } else {
                    setState('error');
                    setErrorMessage(String(t("auth.verificationFailed", "Verification failed. Please try again.")));
                }
            } catch (error: any) {
                setState('error');
                const code = error.response?.data?.code;
                let message = t("auth.verificationFailed", "Verification failed");

                if (code === 'VERIFICATION_TOKEN_EXPIRED') {
                    message = t("auth.verificationLinkExpired", "The verification link has expired. Please request a new one.");
                } else if (code === 'VERIFICATION_TOKEN_USED') {
                    message = t("auth.verificationLinkUsed", "This verification link has already been used.");
                } else if (code === 'VERIFICATION_TOKEN_NOT_FOUND' || code === 'VERIFICATION_INVALID_FORMAT') {
                    message = t("auth.verificationLinkInvalid", "The verification link is invalid.");
                } else {
                    message = error.response?.data?.error || error.message || message;
                }

                setErrorMessage(message);
            }
        };

        verifyEmail();
    }, [token]);



    return (
        <div className="min-h-screen bg-background-page flex flex-col">
            {/* Navigation */}
            <nav className="container-custom py-4 px-4 sm:py-6 sm:px-6">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <Logo className="w-6 h-6" />
                        <span className="text-lg sm:text-2xl font-medium font-display text-text-primary">
                            Dashbal
                        </span>
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {/* Loading State */}
                    {state === 'loading' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            <div className="w-16 h-16 mx-auto mb-6">
                                <div className="w-16 h-16 border-4 border-accent-primary/30 border-t-accent-primary rounded-full animate-spin" />
                            </div>
                            <div className="flex justify-center mb-8 h-8">
                                <h1 className="relative text-2xl font-semibold text-text-primary dark:text-neutral-100">
                                    <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                        <Logo className="w-7 h-7" />
                                        <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_de24d260.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_de24d260.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                    </div>
                                    {t("auth.verifyingEmail", "Verifying your email...")}
                                </h1>
                            </div>
                            <p className="text-neutral-600 dark:text-neutral-400">
                                {t("auth.pleaseWaitVerifying", "Please wait while we verify your email address.")}
                            </p>
                        </div>
                    )}



                    {/* Error State */}
                    {state === 'error' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            <div className="w-20 h-20 bg-error-100 dark:bg-error-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="w-10 h-10 text-error-600 dark:text-error-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_6fc8aad2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_6fc8aad2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>
                            <h1 className="text-2xl font-semibold text-text-primary mb-2">
                                {t("auth.verificationFailed", "Verification Failed")}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                                {errorMessage}
                            </p>
                            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 mb-6">
                                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                    {t("auth.thisCouldHappenIf", "This could happen if:")}
                                </p>
                                <ul className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 space-y-1">
                                    <li>• {t("auth.linkExpired", "The verification link has expired")}</li>
                                    <li>• {t("auth.linkAlreadyUsed", "The link was already used")}</li>
                                    <li>• {t("auth.linkInvalid", "The link is invalid")}</li>
                                </ul>
                            </div>
                            {isAuthenticated && (
                                <button
                                    onClick={() => navigate('/boards')}
                                    className="btn-primary w-full py-3 mb-3"
                                >
                                    {t("actions.goToDashboard", "Go to Dashboard")}
                                </button>
                            )}
                            {resendSuccess && (
                                <div className="bg-success-50 dark:bg-success-900/30 border border-success-200 dark:border-success-800 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-success-700 dark:text-success-400">
                                        {t("auth.verificationEmailSent", "Verification email sent! Check your inbox.")}
                                    </p>
                                </div>
                            )}

                            {isAuthenticated ? (
                                <button
                                    onClick={handleResend}
                                    disabled={isResending || cooldownSeconds > 0}
                                    className={`btn-secondary w-full py-3 inline-block text-center mb-3 ${cooldownSeconds > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isResending ? (
                                        <div className="flex items-center justify-center">
                                            <div className="spinner mr-2" />
                                            {t("actions.loading", "Loading...")}
                                        </div>
                                    ) : cooldownSeconds > 0 ? (
                                        t("auth.resendIn", { seconds: cooldownSeconds, defaultValue: `Resend in ${cooldownSeconds}s` })
                                    ) : (
                                        t("auth.requestNewVerification", "Request New Verification Email")
                                    )}
                                </button>
                            ) : (
                                <Link
                                    to="/?modal=login"
                                    className="btn-secondary w-full py-3 inline-block text-center"
                                >
                                    {t("auth.signInToResend", "Sign In to Resend")}
                                </Link>
                            )}
                        </div>
                    )}

                    {/* No Token State */}
                    {state === 'no-token' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            <div className="w-20 h-20 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="w-10 h-10 text-warning-600 dark:text-warning-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_85f8f3e1.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_85f8f3e1.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>
                            <h1 className="text-2xl font-semibold text-text-primary mb-2">
                                {t("auth.verificationNoToken", "Missing Verification Token")}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                                {t("auth.noVerificationTokenMessage", "No verification token was found in the URL. Please click the link in your email again, or request a new verification email.")}
                            </p>
                            <Link
                                to="/?modal=login"
                                className="btn-primary w-full py-3 inline-block text-center"
                            >
                                {t("auth.goToLogin", "Go to Login")}
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-neutral-200 dark:border-neutral-800 py-6">
                <div className="container-custom text-center">
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                        © {new Date().getFullYear()} Dashbal. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default VerifyEmailPage;
